// Cisco product/architecture taxonomy — a finer-grained sub-layer under the 8
// technology-footprint UseCaseIds. Sourced from a customer "Cisco Ready"-style
// export (business entities a customer runs); the exact upload format is
// expected to drift over time (and may eventually come from a live API instead
// of a file), so this catalog — not the parser — is the stable contract: every
// importer (Excel today, manual picker, a future API) resolves free-text labels
// down to these ids.
//
// SELF-CONTAINED port. The types below were inlined verbatim from the example
// app's modules/shared/types.ts so this file has no external imports.

// Cisco technology footprint — repurposes the "use case" mechanism (adaptive
// threat/question filtering keyed by this id) for "which infrastructure do
// you run" instead of "which AI adoption pattern applies".
export type UseCaseId =
  | "nexus_dc"
  | "meraki"
  | "catalyst_sdwan"
  | "catalyst_switching"
  | "cisco_firewall"
  | "collaboration"
  | "wifi"
  // Free-text "Other / non-Cisco infrastructure" — scored with a conservative
  // default profile, same role as the old "other_custom".
  | "other_non_cisco";

export type EntityCategoryId = string;
export type EntityItemId = string;

export type EntityCategory = {
  id: EntityCategoryId;
  label: string;
  /** Rollup target when a member item has no `useCaseIdOverride`. `null` =
   *  captured for display only, never auto-selects or scores a UseCase. */
  defaultUseCaseId: UseCaseId | null;
};

export type EntityItem = {
  id: EntityItemId;
  label: string;
  categoryId: EntityCategoryId;
  /** Match variants for the Excel-import fuzzy matcher (exact/alias/fuzzy). */
  aliases: string[];
  /** Overrides the category's `defaultUseCaseId` for this one item — e.g.
   *  every Meraki-branded item rolls up to `meraki` regardless of which
   *  Cisco-taxonomy category it was filed under upstream. */
  useCaseIdOverride?: UseCaseId | null;
  /** Flags a legacy/EOL-prone product line for display-only "informational"
   *  treatment in the report — NOT wired into scoring. */
  eolRisk?: boolean;
};

/** Minimal committed-selection shape `deriveUseCaseIds` reads. */
export type EntitySelection = {
  itemId: EntityItemId;
};

export const ENTITY_CATEGORIES: EntityCategory[] = [
  { id: "cisco_compute", label: "Cisco Compute", defaultUseCaseId: null },
  { id: "collaboration", label: "Collaboration", defaultUseCaseId: "collaboration" },
  { id: "data_center_networking", label: "Data Center Networking", defaultUseCaseId: "nexus_dc" },
  { id: "enterprise_routing", label: "Enterprise Routing", defaultUseCaseId: "catalyst_sdwan" },
  { id: "enterprise_switching", label: "Enterprise Switching", defaultUseCaseId: "catalyst_switching" },
  { id: "internet_infrastructure", label: "Internet Infrastructure", defaultUseCaseId: null },
  { id: "network_assurance", label: "Network Assurance", defaultUseCaseId: null },
  { id: "other", label: "Other", defaultUseCaseId: "other_non_cisco" },
  { id: "other_enterprise_networking", label: "Other Enterprise Networking", defaultUseCaseId: "other_non_cisco" },
  { id: "security", label: "Security", defaultUseCaseId: null },
  { id: "observability", label: "Observability / APM", defaultUseCaseId: null },
  { id: "wireless", label: "Wireless", defaultUseCaseId: "wifi" },
];

// Rollup caveats surfaced verbatim in the entity-review/confirmation screen
// when an item resolves to this UseCaseId via a category default (not an
// explicit per-item override) — i.e. a "closest fit, please confirm" note.
export const CATEGORY_ROLLUP_CAVEATS: Partial<Record<EntityCategoryId, string>> = {
  enterprise_routing:
    "Mapped to Catalyst SD-WAN as the closest fit — edit if this routing gear isn't SD-WAN-controller-based.",
};

const meraki = (item: Omit<EntityItem, "useCaseIdOverride" | "categoryId"> & { categoryId: EntityCategoryId }): EntityItem => ({
  ...item,
  useCaseIdOverride: "meraki",
});

export const ENTITY_ITEMS: EntityItem[] = [
  // Cisco Compute — no clean UseCaseId home (display-only)
  { id: "hyper_converged", label: "Hyper Converged", categoryId: "cisco_compute", aliases: ["HyperFlex", "HCI"] },
  { id: "intersight", label: "Intersight", categoryId: "cisco_compute", aliases: ["Cisco Intersight"] },
  { id: "servers", label: "Servers", categoryId: "cisco_compute", aliases: ["UCS", "Unified Computing System", "Cisco UCS Servers"] },

  // Collaboration -> collaboration
  { id: "cloud_contact_center", label: "Cloud Contact Center", categoryId: "collaboration", aliases: ["Webex Contact Center", "CCaaS"] },
  { id: "cloud_webex_suite", label: "Cloud Webex Suite", categoryId: "collaboration", aliases: ["Webex Suite", "Webex Cloud Calling"] },
  { id: "cpaas", label: "CPaaS", categoryId: "collaboration", aliases: ["Webex Connect"] },
  { id: "onprem_contact_center", label: "On-Prem Contact Center", categoryId: "collaboration", aliases: ["UCCE", "Unified Contact Center Enterprise", "Packaged CCE"] },
  { id: "onprem_webex_suite", label: "On-Prem Webex Suite", categoryId: "collaboration", aliases: ["CUCM", "Unified Communications Manager"] },
  { id: "phone_headset_devices", label: "Phone and Headset Devices", categoryId: "collaboration", aliases: ["IP Phones", "Webex Desk Phones", "Headsets"] },
  { id: "video_devices", label: "Video Devices", categoryId: "collaboration", aliases: ["Webex Room Devices", "Video Endpoints"] },

  // Data Center Networking -> nexus_dc (legacy families flagged eolRisk, display-only)
  { id: "nx1k", label: "NX1K", categoryId: "data_center_networking", aliases: ["Nexus 1000", "Nexus 1000v", "N1Kv"], eolRisk: true },
  { id: "nx2_4_5_6k", label: "NX2/4/5/6K", categoryId: "data_center_networking", aliases: ["Nexus 2000", "Nexus 4000", "Nexus 5000", "Nexus 6000", "N2K", "N5K", "N6K"], eolRisk: true },
  { id: "nx3k", label: "NX3K", categoryId: "data_center_networking", aliases: ["Nexus 3000", "N3K"], eolRisk: true },
  { id: "nx7k", label: "NX7K", categoryId: "data_center_networking", aliases: ["Nexus 7000", "N7K"], eolRisk: true },
  { id: "nx9k", label: "NX9K", categoryId: "data_center_networking", aliases: ["Nexus 9000", "N9K", "Nexus 9K Series", "Nexus 9000 Series"] },
  { id: "optics_dc_allocation", label: "Optics - DC Allocation", categoryId: "data_center_networking", aliases: ["Data Center Optics"] },
  { id: "san", label: "SAN", categoryId: "data_center_networking", aliases: ["Storage Area Network", "MDS"] },

  // Enterprise Routing -> catalyst_sdwan (caveat), Meraki SD-WAN -> meraki
  { id: "ent_access_routing", label: "Ent. Access Routing", categoryId: "enterprise_routing", aliases: ["ISR", "Integrated Services Router", "Branch Routing"] },
  { id: "ent_edge_routing", label: "Ent. Edge Routing", categoryId: "enterprise_routing", aliases: ["ASR", "Aggregation Services Router", "Edge Routing"] },
  { id: "iiot_routing", label: "IIoT Routing", categoryId: "enterprise_routing", aliases: ["IR1101", "Industrial Routing"] },
  meraki({ id: "meraki_sdwan", label: "Meraki SD-WAN", categoryId: "enterprise_routing", aliases: ["Meraki vMX", "Meraki MX SD-WAN"] }),

  // Enterprise Switching -> catalyst_switching, Meraki Switching -> meraki
  { id: "ent_switching_other", label: "Ent. - Other", categoryId: "enterprise_switching", aliases: [] },
  { id: "ent_sb_switching", label: "Ent. Small Business - Switching", categoryId: "enterprise_switching", aliases: ["Cisco Business Switches", "SMB Switching"] },
  { id: "ent_switching_access", label: "Ent. Switching - Access", categoryId: "enterprise_switching", aliases: ["Catalyst 9200", "Catalyst 9300", "Access Switching"] },
  { id: "ent_switching_connected_platforms", label: "Ent. Switching - Connected Platforms", categoryId: "enterprise_switching", aliases: ["Catalyst 9000 Connected Platforms"] },
  { id: "ent_switching_core", label: "Ent. Switching - Core", categoryId: "enterprise_switching", aliases: ["Catalyst 9400", "Catalyst 9500", "Core Switching"] },
  { id: "iiot_switching", label: "IIoT Switching", categoryId: "enterprise_switching", aliases: ["Industrial Switching", "IE Switches"] },
  meraki({ id: "meraki_switching", label: "Meraki Switching", categoryId: "enterprise_switching", aliases: ["Meraki MS", "Meraki MS Switches"] }),
  { id: "optics_ent_networking_allocation", label: "Optics - Ent. Networking Allocation", categoryId: "enterprise_switching", aliases: ["Enterprise Optics"] },

  // Internet Infrastructure — SP/carrier gear, no clean home (display-only)
  { id: "sp_automation", label: "Automation", categoryId: "internet_infrastructure", aliases: [] },
  { id: "sp_control_center", label: "Control Center", categoryId: "internet_infrastructure", aliases: [] },
  { id: "sp_mobility", label: "Mobility", categoryId: "internet_infrastructure", aliases: [] },
  { id: "sp_optical", label: "OPTICAL", categoryId: "internet_infrastructure", aliases: [] },
  { id: "sp_optics_routing_allocation", label: "Optics - SP Routing Allocation", categoryId: "internet_infrastructure", aliases: [] },
  { id: "sp_access_aggregation", label: "SP Access & Aggregation", categoryId: "internet_infrastructure", aliases: [] },
  { id: "sp_core_routing", label: "SP Core Routing", categoryId: "internet_infrastructure", aliases: [] },
  { id: "sp_edge_routing", label: "SP Edge Routing", categoryId: "internet_infrastructure", aliases: [] },
  { id: "sp_network_assurance", label: "SP Network Assurance", categoryId: "internet_infrastructure", aliases: [] },

  // Network Assurance — SaaS monitoring, no clean home (display-only)
  { id: "thousandeyes", label: "ThousandEyes", categoryId: "network_assurance", aliases: ["Cisco ThousandEyes"] },

  // Other -> other_non_cisco (clean fallback matching its literal name)
  { id: "data_analytics", label: "Data & Analytics", categoryId: "other", aliases: [] },
  { id: "other_other", label: "Other_Other", categoryId: "other", aliases: ["Uncategorized", "Miscellaneous"] },

  // Other Enterprise Networking — both items are Meraki-branded -> meraki
  meraki({ id: "meraki_iot", label: "Meraki IOT", categoryId: "other_enterprise_networking", aliases: ["CNIOT", "Meraki IoT", "Meraki MT"] }),
  meraki({ id: "meraki_vision", label: "Meraki Vision", categoryId: "other_enterprise_networking", aliases: ["CNVSN", "Meraki MV", "Meraki Smart Cameras"] }),

  // Security — mixed: perimeter/segmentation-adjacent items map to
  // cisco_firewall; IAM and threat-intel/detection tooling have no home yet.
  { id: "iam", label: "Identity & Access Management", categoryId: "security", aliases: ["Duo", "ISE", "Identity Services Engine"] },
  { id: "network_security", label: "Network Security", categoryId: "security", aliases: ["Secure Firewall", "ASA", "Firepower"], useCaseIdOverride: "cisco_firewall" },
  { id: "secure_service_edge", label: "Secure Service Edge", categoryId: "security", aliases: ["Cisco Umbrella", "SSE", "Secure Access"], useCaseIdOverride: "cisco_firewall" },
  { id: "threat_intel_detection_response", label: "Threat Intelligence, Detection, & Response", categoryId: "security", aliases: ["XDR", "Secure Endpoint", "Talos"] },

  // Observability / APM — no clean home.
  { id: "splunk", label: "Splunk", categoryId: "observability", aliases: ["Splunk Enterprise", "Splunk Cloud"] },
  { id: "appdynamics", label: "AppDynamics", categoryId: "observability", aliases: ["Cisco AppDynamics", "APM"] },

  // Wireless -> wifi, Meraki Wireless -> meraki
  { id: "cisco_spaces", label: "Cisco Spaces", categoryId: "wireless", aliases: ["DNA Spaces"] },
  { id: "ent_wireless", label: "Ent. - Wireless", categoryId: "wireless", aliases: ["Catalyst 9800", "Aironet", "Wireless LAN Controller"] },
  meraki({ id: "meraki_wireless", label: "Meraki Wireless", categoryId: "wireless", aliases: ["Meraki MR", "Meraki Access Points"] }),
];

export function getEntityCategory(id: EntityCategoryId): EntityCategory | undefined {
  return ENTITY_CATEGORIES.find((c) => c.id === id);
}

export function getEntityItem(id: EntityItemId): EntityItem | undefined {
  return ENTITY_ITEMS.find((i) => i.id === id);
}

export function itemsByCategory(categoryId: EntityCategoryId): EntityItem[] {
  return ENTITY_ITEMS.filter((i) => i.categoryId === categoryId);
}

/** Resolves a single item to the UseCaseId it should auto-select, honoring
 *  the item-level override before the category default. `null` = display-only. */
export function resolveUseCaseId(item: EntityItem): UseCaseId | null {
  if (item.useCaseIdOverride !== undefined) return item.useCaseIdOverride;
  const category = getEntityCategory(item.categoryId);
  return category?.defaultUseCaseId ?? null;
}

/** Derives the (deduped) set of UseCaseIds implied by a committed entity
 *  selection — the read-time join used to auto-select technology-footprint
 *  domains without duplicating that relationship into `UseCaseSelection`. */
export function deriveUseCaseIds(entities: EntitySelection[]): UseCaseId[] {
  const ids = new Set<UseCaseId>();
  for (const entity of entities) {
    const item = getEntityItem(entity.itemId);
    if (!item) continue;
    const useCaseId = resolveUseCaseId(item);
    if (useCaseId) ids.add(useCaseId);
  }
  return [...ids];
}
